// src/lib/doubleElimination.js
// Double elimination bracket templates + routing for 4 and 8 team tournaments.
//
// Each generated match has:
//   - bracket: "WB" | "LB" | "GF"
//   - round + matchNumber (unique within bracket)
//   - winnerAdvancesTo: { bracket, round, matchNumber, slot } | null
//   - loserDropsTo:    { bracket, round, matchNumber, slot } | null   (only WB matches)
//
// The slot is "team1" or "team2" — tells auto-advance WHICH side of the next match to fill.

// ─── Match templates ─────────────────────────────────────────────────────────
// Each entry: { bracket, round, matchNumber, label?, winnerAdvancesTo, loserDropsTo }
// `label` is for display (e.g. "WB Final", "Grand Final")
// `team1Source` / `team2Source` (optional) identify where the slot's player comes from
//   for matches that aren't filled at generation time. Used for "TBD" labels.

// ── 4-team double elim ── (6 matches total)
const TEMPLATE_4 = [
  // ── Winners Bracket ──
  { bracket: "WB", round: 1, matchNumber: 1,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 1, slot: "team1" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 1, slot: "team1" } },
  { bracket: "WB", round: 1, matchNumber: 2,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 1, slot: "team2" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 1, slot: "team2" } },
  { bracket: "WB", round: 2, matchNumber: 1, label: "WB Final",
    winnerAdvancesTo: { bracket: "GF", round: 1, matchNumber: 1, slot: "team1" },
    loserDropsTo:    { bracket: "LB", round: 2, matchNumber: 1, slot: "team2" } },
  // ── Losers Bracket ──
  { bracket: "LB", round: 1, matchNumber: 1,
    winnerAdvancesTo: { bracket: "LB", round: 2, matchNumber: 1, slot: "team1" } },
  { bracket: "LB", round: 2, matchNumber: 1, label: "LB Final",
    winnerAdvancesTo: { bracket: "GF", round: 1, matchNumber: 1, slot: "team2" } },
  // ── Grand Final ──
  { bracket: "GF", round: 1, matchNumber: 1, label: "Grand Final" },
];

// ── 8-team double elim ── (14 matches total)
const TEMPLATE_8 = [
  // ── Winners Bracket Round 1 (Quarterfinals) ──
  { bracket: "WB", round: 1, matchNumber: 1,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 1, slot: "team1" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 1, slot: "team1" } },
  { bracket: "WB", round: 1, matchNumber: 2,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 1, slot: "team2" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 2, slot: "team1" } },
  { bracket: "WB", round: 1, matchNumber: 3,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 2, slot: "team1" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 2, slot: "team2" } },
  { bracket: "WB", round: 1, matchNumber: 4,
    winnerAdvancesTo: { bracket: "WB", round: 2, matchNumber: 2, slot: "team2" },
    loserDropsTo:    { bracket: "LB", round: 1, matchNumber: 1, slot: "team2" } },

  // ── Winners Bracket Round 2 (Semifinals) ──
  { bracket: "WB", round: 2, matchNumber: 1, label: "WB Semifinal 1",
    winnerAdvancesTo: { bracket: "WB", round: 3, matchNumber: 1, slot: "team1" },
    // Loser drops to LB R2 with reversal: WB SF1 loser → LB R2 M2
    loserDropsTo:    { bracket: "LB", round: 2, matchNumber: 2, slot: "team2" } },
  { bracket: "WB", round: 2, matchNumber: 2, label: "WB Semifinal 2",
    winnerAdvancesTo: { bracket: "WB", round: 3, matchNumber: 1, slot: "team2" },
    // WB SF2 loser → LB R2 M1 (reversed)
    loserDropsTo:    { bracket: "LB", round: 2, matchNumber: 1, slot: "team2" } },

  // ── Winners Bracket Final ──
  { bracket: "WB", round: 3, matchNumber: 1, label: "WB Final",
    winnerAdvancesTo: { bracket: "GF", round: 1, matchNumber: 1, slot: "team1" },
    loserDropsTo:    { bracket: "LB", round: 4, matchNumber: 1, slot: "team2" } },

  // ── Losers Bracket Round 1 ──
  // Reversal: M1=L(WB1) vs L(WB4), M2=L(WB2) vs L(WB3)
  { bracket: "LB", round: 1, matchNumber: 1,
    winnerAdvancesTo: { bracket: "LB", round: 2, matchNumber: 1, slot: "team1" } },
  { bracket: "LB", round: 1, matchNumber: 2,
    winnerAdvancesTo: { bracket: "LB", round: 2, matchNumber: 2, slot: "team1" } },

  // ── Losers Bracket Round 2 ──
  { bracket: "LB", round: 2, matchNumber: 1,
    winnerAdvancesTo: { bracket: "LB", round: 3, matchNumber: 1, slot: "team1" } },
  { bracket: "LB", round: 2, matchNumber: 2,
    winnerAdvancesTo: { bracket: "LB", round: 3, matchNumber: 1, slot: "team2" } },

  // ── Losers Bracket Semifinal ──
  { bracket: "LB", round: 3, matchNumber: 1, label: "LB Semifinal",
    winnerAdvancesTo: { bracket: "LB", round: 4, matchNumber: 1, slot: "team1" } },

  // ── Losers Bracket Final ──
  { bracket: "LB", round: 4, matchNumber: 1, label: "LB Final",
    winnerAdvancesTo: { bracket: "GF", round: 1, matchNumber: 1, slot: "team2" } },

  // ── Grand Final ──
  { bracket: "GF", round: 1, matchNumber: 1, label: "Grand Final" },
];

const TEMPLATES = {
  4: TEMPLATE_4,
  8: TEMPLATE_8,
};

export const SUPPORTED_DOUBLE_ELIM_SIZES = Object.keys(TEMPLATES).map(Number);

export function isDoubleElimSupported(teamCount) {
  return TEMPLATES.hasOwnProperty(teamCount);
}

/**
 * Build the array of match documents for a double-elim tournament.
 *
 * @param {Array<{teamName: string, roster: string[]}>} participants
 * @param {boolean} isCDL - whether to attach roster fields
 * @returns {Array<object>} match documents ready for batch insert (no id yet)
 */
export function generateDoubleElimMatches(participants, isCDL = false) {
  const teamCount = participants.length;
  const template = TEMPLATES[teamCount];
  if (!template) {
    throw new Error(
      `Double elimination only supports ${SUPPORTED_DOUBLE_ELIM_SIZES.join(", ")} teams (got ${teamCount}).`
    );
  }

  return template.map((m) => {
    const doc = {
      bracket: m.bracket,
      round: m.round,
      matchNumber: m.matchNumber,
      label: m.label || "",
      team1Name: "",
      team1Score: 0,
      team2Name: "",
      team2Score: 0,
      winner: "",
      status: "pending",
      winnerAdvancesTo: m.winnerAdvancesTo || null,
      loserDropsTo: m.loserDropsTo || null,
    };
    if (isCDL) {
      doc.team1Roster = [];
      doc.team2Roster = [];
    }

    // Pre-fill WB Round 1 from participants
    if (m.bracket === "WB" && m.round === 1) {
      const pairIndex = m.matchNumber - 1;
      const t1 = participants[pairIndex * 2];
      const t2 = participants[pairIndex * 2 + 1];
      if (t1) {
        doc.team1Name = t1.teamName;
        if (isCDL) doc.team1Roster = t1.roster || [];
      }
      if (t2) {
        doc.team2Name = t2.teamName;
        if (isCDL) doc.team2Roster = t2.roster || [];
      }
    }

    return doc;
  });
}

/**
 * Special routing: for 8-team LB Round 1, team1 of M1 is the loser of WB R1 M1,
 * and team2 of M1 is the loser of WB R1 M4 (reversal).
 * The loserDropsTo on each WB R1 match handles this automatically — the routing
 * is encoded in the templates above.
 */
